import { Dispatch } from '@reduxjs/toolkit';
import { User } from 'auth/user';
import { CenteredModal } from 'components/CenteredModal';
import { ErrorModal } from 'components/ErrorModal';
import { Scaffold } from 'components/Scaffold';
import { SuccessModal } from 'components/SuccessModal';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyAction } from 'redux';
import {
  fetchCompaniesWithMpidsAction,
  fetchRiskAccountsAction,
  fetchRiskAllOCCAction,
  fetchRiskCredirLimitsMatrixAction,
  fetchRiskEnginesAction,
  fetchRiskMatrixAction,
} from 'redux/actions/riskActions';
import { ApplicationState } from 'redux/applicationState';
import { AsyncAction } from 'redux/asyncAction';
import {
  resetErrorMessage,
  resetSuccessMessage,
  riskSelector,
  setIsDeletingRiskAccount,
} from 'redux/reducers/riskReducer';
import { MatrixTabs } from 'routes/Risk/components/MatrixTabs';
import { RiskHeader } from 'routes/Risk/components/RiskHeader';

export const Risk: React.FC = (): React.ReactElement => {
  const dispatch = useDispatch<Dispatch<AsyncAction | AnyAction>>();
  const risk = useSelector(riskSelector);
  const currentUser = useSelector<ApplicationState, User>((state) => state.currentUser.user);
  const canManageRisk = currentUser.hasAnyRight('MANAGE_RISK');
  const canViewRisk = currentUser.hasAnyRight('VIEW_RISK', 'MANAGE_RISK');

  const closeSuccessModal = React.useCallback((): void => {
    dispatch(resetSuccessMessage());
    dispatch(setIsDeletingRiskAccount(false));
  }, [dispatch]);

  const closeErrorModal = React.useCallback((): void => {
    dispatch(resetErrorMessage());
  }, [dispatch]);

  React.useEffect((): void => {
    if (!canViewRisk || risk.savingRiskAccount) return;

    dispatch(fetchRiskMatrixAction(risk.sessionID, risk.clientID));
  }, [canViewRisk, dispatch, risk.clientID, risk.sessionID, risk.savingRiskAccount]);

  React.useEffect((): void => {
    if (!canViewRisk) return;

    dispatch(fetchRiskEnginesAction());
  }, [canViewRisk, dispatch]);

  React.useEffect((): void => {
    if (!canViewRisk || risk.savingRiskAccount) return;
    dispatch(fetchRiskAccountsAction(risk.clientID));
  }, [canViewRisk, dispatch, risk.clientID, risk.savingRiskAccount]);

  React.useEffect((): void => {
    if (!canViewRisk || risk.savingRiskAccount) return;
    dispatch(fetchRiskAllOCCAction());
  }, [canViewRisk, dispatch, risk.savingRiskAccount]);

  React.useEffect((): void => {
    if (!canViewRisk || risk.savingRiskAccount) return;
    dispatch(fetchCompaniesWithMpidsAction());
  }, [canViewRisk, dispatch, risk.savingRiskAccount]);

  React.useEffect((): void => {
    if (!canViewRisk || risk.savingRiskAccount) return;
    dispatch(fetchRiskCredirLimitsMatrixAction(risk.clientID));
  }, [canViewRisk, dispatch, risk.clientID, risk.savingRiskAccount]);

  if (!canViewRisk) {
    return (
      <Scaffold title="Risk">
        <div style={{ padding: 24 }}>You do not have permission to view risk data.</div>
      </Scaffold>
    );
  }

  return (
    <Scaffold title="Risk">
      <RiskHeader disabled={risk.savingRiskMatrix || !canManageRisk} />
      {risk.clientID && (
        <>
          <MatrixTabs readOnly={!canManageRisk} />
        </>
      )}
      <CenteredModal open={!!risk.errorMessage} onClose={closeErrorModal}>
        <ErrorModal
          title="There was an error"
          message="An error occurred when executing your last operation"
          error={risk.errorMessage}
          onClose={closeErrorModal}
        />
      </CenteredModal>
      <CenteredModal open={!!risk.successMessage} onClose={closeSuccessModal}>
        <SuccessModal
          title="Operation Completed"
          message={risk.successMessage}
          onClose={closeSuccessModal}
        />
      </CenteredModal>
    </Scaffold>
  );
};
